import { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, XAxis, YAxis,
} from "recharts";
import {
  ClipboardList, CalendarDays, CheckCircle2, Clock, Hash,
} from "lucide-react";

const PALETTE = ["#AF3C3C", "#0F766E", "#1E3A8A", "#A16207", "#7C2D12", "#9333EA", "#0891B2"];

const fmtMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-IN", {
    month: "long", year: "numeric",
  });
};

const complianceColor = (pct) => {
  if (pct >= 80) return "bg-emerald-100 text-emerald-800 border-emerald-300";
  if (pct >= 50) return "bg-amber-100 text-amber-800 border-amber-300";
  if (pct >= 25) return "bg-orange-100 text-orange-800 border-orange-300";
  return "bg-red-100 text-red-800 border-red-300";
};

export default function SMChecklistMonthly() {
  const today = new Date();
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

  const [smList, setSmList] = useState([]);
  const [smId, setSmId] = useState("");
  const [month, setMonth] = useState(thisMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSmList = useCallback(async () => {
    try {
      const { data } = await api.get("/sm-checklist/sm-list");
      setSmList(data || []);
      if (data && data.length > 0 && !smId) setSmId(data[0].id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  }, [smId]);

  const loadData = useCallback(async () => {
    if (!smId || !month) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/sm-checklist/monthly?sm_id=${smId}&month=${month}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [smId, month]);

  useEffect(() => { loadSmList(); }, [loadSmList]);
  useEffect(() => { loadData(); }, [loadData]);

  const donutData = useMemo(() => {
    if (!data) return [];
    return data.categories
      .filter((c) => c.total_minutes > 0)
      .map((c) => ({ name: c.category, value: c.total_minutes }));
  }, [data]);

  return (
    <div className="space-y-5" data-testid="sm-monthly-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400 flex items-center gap-1.5">
          <ClipboardList className="h-3 w-3" /> Senior Management
        </div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Chivo" }}>
          Monthly Summary
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Per-category compliance, total time spent and miss breakdown for the selected SM.
        </p>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
            Senior Mgmt
          </Label>
          <Select value={smId} onValueChange={setSmId}>
            <SelectTrigger className="h-8 w-56 text-xs" data-testid="monthly-sm-picker">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {smList.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="w-px h-5 bg-slate-300" />
          <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1.5">
            <CalendarDays className="h-3 w-3" /> Month
          </Label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="h-8 w-40 text-xs"
            data-testid="monthly-month-input"
          />
          <div className="text-xs text-slate-600 ml-1">
            <b className="text-slate-900">{fmtMonth(month)}</b>
          </div>
        </CardContent>
      </Card>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}

      {!loading && data && (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Card>
              <CardContent className="p-3">
                <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1">
                  <CalendarDays className="h-3 w-3" /> Working Days
                </div>
                <div className="text-2xl font-semibold mt-0.5 tabular-nums">{data.working_days}</div>
                <div className="text-[10px] text-slate-400">Mon → Sat</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1">
                  <Hash className="h-3 w-3" /> Expected Slots
                </div>
                <div className="text-2xl font-semibold mt-0.5 tabular-nums">{data.totals.expected_slots}</div>
                <div className="text-[10px] text-slate-400">tasks × working days</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <div className="text-[10px] uppercase text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Done
                </div>
                <div className="text-2xl font-semibold mt-0.5 tabular-nums text-emerald-700">{data.totals.done}</div>
                <div className="text-[10px] text-emerald-700/70">
                  <span className={`inline-block px-1.5 py-0.5 rounded border ${complianceColor(data.totals.compliance_pct)}`}>
                    {data.totals.compliance_pct}% compliance
                  </span>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-3">
                <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Total Hours
                </div>
                <div className="text-2xl font-semibold mt-0.5 tabular-nums">{data.totals.total_hours}</div>
                <div className="text-[10px] text-slate-400">logged time</div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Donut: time spent per category */}
            <Card>
              <CardContent className="p-4">
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Time allocation</div>
                  <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
                    Hours per category
                  </div>
                </div>
                {donutData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie
                        data={donutData}
                        cx="50%" cy="50%"
                        innerRadius={50}
                        outerRadius={85}
                        paddingAngle={2}
                        dataKey="value"
                        label={(d) => `${d.name} (${Math.round(d.value / 60)}h)`}
                      >
                        {donutData.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${Math.round(v / 60 * 10) / 10}h (${v} min)`} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-40 text-center text-slate-400 text-xs flex items-center justify-center">
                    No logged time this month
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bar: compliance per category */}
            <Card>
              <CardContent className="p-4">
                <div className="mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Compliance</div>
                  <div className="font-semibold text-slate-900 text-sm" style={{ fontFamily: "Chivo" }}>
                    Done % per category
                  </div>
                </div>
                {data.categories.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart data={data.categories} layout="vertical" margin={{ left: 10 }}>
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                      <YAxis type="category" dataKey="category" width={70} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => `${v}%`} />
                      <Bar dataKey="compliance_pct" radius={[0, 4, 4, 0]}>
                        {data.categories.map((_, i) => (
                          <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-40 text-center text-slate-400 text-xs flex items-center justify-center">
                    No data
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detail table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-right">Tasks</th>
                      <th className="px-3 py-2 text-right">Expected Slots</th>
                      <th className="px-3 py-2 text-right text-emerald-700">Done</th>
                      <th className="px-3 py-2 text-right text-amber-700">In Progress</th>
                      <th className="px-3 py-2 text-right text-slate-700">Skipped</th>
                      <th className="px-3 py-2 text-right text-red-700">Pending</th>
                      <th className="px-3 py-2 text-right">Hours</th>
                      <th className="px-3 py-2 text-right">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.categories.length === 0 && (
                      <tr><td colSpan={9} className="px-3 py-8 text-center text-slate-400">No tasks configured</td></tr>
                    )}
                    {data.categories.map((c) => (
                      <tr key={c.category} className="border-t border-slate-100" data-testid={`monthly-cat-${c.category}`}>
                        <td className="px-3 py-2 font-medium text-slate-900">{c.category}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{c.task_count}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-500">{c.expected_slots}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-700 font-semibold">{c.done}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-amber-700">{c.in_progress}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-slate-700">{c.skipped}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-red-700">{c.pending}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{Math.round(c.total_minutes / 6) / 10}</td>
                        <td className="px-3 py-2 text-right">
                          <span className={`inline-block px-2 py-0.5 rounded border text-xs font-semibold tabular-nums ${complianceColor(c.compliance_pct)}`}>
                            {c.compliance_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
